/**
 * Compress image client-side before uploading
 * MAXIMUM QUALITY for presentation images
 * Only compresses if absolutely necessary (over 5MB)
 * Preserves transparency for PNGs
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxSizeMB = 5,           // ✅ Only compress if > 5MB
    maxWidthOrHeight = 2400, // ✅ Higher resolution (was 1920)
    quality = 0.98,          // ✅ Near-lossless quality (was 0.95)
    preserveTransparency = true
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // ✅ NEW: Check if image even needs compression
        const fileSizeMB = file.size / 1024 / 1024;
        const needsResize = img.width > maxWidthOrHeight || img.height > maxWidthOrHeight;
        
        // ✅ If file is under 5MB AND dimensions are good, skip compression entirely
        if (fileSizeMB <= maxSizeMB && !needsResize) {
          
          resolve(file); // Return original file
          return;
        }
        
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // ✅ Preserve aspect ratio correctly
        const aspectRatio = width / height;
        
        if (width > height) {
          if (width > maxWidthOrHeight) {
            width = maxWidthOrHeight;
            height = Math.round(width / aspectRatio);
          }
        } else {
          if (height > maxWidthOrHeight) {
            height = maxWidthOrHeight;
            width = Math.round(height * aspectRatio);
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d', {
          alpha: preserveTransparency,
          willReadFrequently: false
        });
        
        // ✅ Detect format - prefer PNG for quality, only use JPEG if originally JPEG
        const isPNG = file.type === 'image/png';
        const isJPEG = file.type === 'image/jpeg' || file.type === 'image/jpg';
        
        // ✅ Use PNG for everything UNLESS original was JPEG AND over 5MB
        let outputFormat = 'image/png'; // Default to PNG for quality
        if (isJPEG && fileSizeMB > maxSizeMB) {
          outputFormat = 'image/jpeg'; // Only use JPEG if compressing large JPEG
        }
        
        // Only add white background for JPEG output
        if (outputFormat === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }
        
        // ✅ Maximum quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // ✅ UPDATED: More lenient compression - only compress if significantly over limit
        const tryCompress = (currentQuality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas to Blob conversion failed'));
                return;
              }
              
              const sizeMB = blob.size / 1024 / 1024;
              
              // ✅ Only reduce quality if WAY over limit (7MB+) AND quality is still very high
              if (sizeMB > 7 && currentQuality > 0.85) {
                
                tryCompress(currentQuality - 0.03); // Tiny steps
                return;
              }
              
              const compressedFile = new File([blob], file.name, {
                type: outputFormat,
                lastModified: Date.now()
              });
              
              
              resolve(compressedFile);
            },
            outputFormat,
            currentQuality
          );
        };
        
        // Start with maximum quality
        tryCompress(quality);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};